import { Controller } from 'stimulus'

import { alerts } from '@blueberry/lib/alerts'
import { performGCSUpload } from '@blueberry/framework/gcs_uploader.js'

// 300MB in bytes, ~3 minutes of iPhone video — same ceiling as the legacy px
// messaging composer this is modelled on.
const MAX_FILE_SIZE = 300 * 1024 * 1024

// Adds a photo/video attachment to a balance message composer (parent reply,
// clinician reply, and both new-thread forms). On file pick it uploads directly
// to GCS — the same flow as `px/controllers/message-input_controller` — and
// *stages* the result: it writes the `gcs_location` + `message_type` into the
// form's hidden inputs and shows a preview chip, WhatsApp-style. The user then
// presses Send to post the attachment, optionally with a caption typed into the
// normal text field. Text-only sends are untouched: the hidden inputs stay empty.
//
// Attach to the <form>, or to a wrapper containing it (e.g. when a full-width
// progress bar sits outside the form row) — the form is resolved either way.
// Hidden inputs `gcsLocation`/`messageType` carry the upload result; `file` is
// the (hidden) picker; `progress` is an optional bar; `preview` is the staged-
// attachment chip; `text` is the caption field (its `required` is relaxed while
// an attachment is staged so a caption stays optional).
export default class extends Controller<HTMLElement> {
  static targets = [
    'file',
    'gcsLocation',
    'messageType',
    'progress',
    'preview',
    'text',
  ]

  declare readonly fileTarget: HTMLInputElement
  declare readonly gcsLocationTarget: HTMLInputElement
  declare readonly messageTypeTarget: HTMLInputElement
  declare readonly hasProgressTarget: boolean
  declare readonly progressTarget: HTMLProgressElement
  declare readonly hasPreviewTarget: boolean
  declare readonly previewTarget: HTMLElement
  declare readonly hasTextTarget: boolean
  declare readonly textTarget: HTMLInputElement | HTMLTextAreaElement

  uploading = false
  private textInitiallyRequired = false
  private objectUrl: string | null = null

  connect() {
    // Remember whether the caption field was required so clear() can restore it
    // (reply composers require text; the dx new-thread textarea does not).
    this.textInitiallyRequired = this.hasTextTarget && this.textTarget.required
  }

  disconnect() {
    this.revokeObjectUrl()
  }

  open() {
    if (this.uploading) return
    this.fileTarget.click()
  }

  async upload() {
    const file = this.fileTarget.files?.[0]
    if (!file) return

    const [kind] = file.type.toUpperCase().split('/')
    if (kind !== 'IMAGE' && kind !== 'VIDEO') {
      alerts.error('Only images and videos can be attached.')
      this.fileTarget.value = ''
      return
    }

    this.uploading = true
    this.setProgress(1)

    let result
    try {
      result = await performGCSUpload({
        file,
        maxFileSize: MAX_FILE_SIZE,
        onProgress: (p) => this.setProgress(Math.max(1, Math.floor(p * 100))),
      })
    } catch (e) {
      result = {
        type: 'error' as const,
        data: (e as { data?: string })?.data ?? 'upload failed',
      }
    } finally {
      this.uploading = false
      this.fileTarget.value = ''
      this.hideProgress()
    }

    if (result.type === 'error') {
      alerts.error(`Could not upload media. ${result.data}`)
      return
    }

    this.stage(result.data, kind, file)
  }

  // Stash the uploaded attachment and surface it as a removable preview chip.
  // The caption field stays untouched (its text becomes the caption) but its
  // `required` is relaxed so a caption-less send is allowed.
  private stage(gcsLocation: string, kind: 'IMAGE' | 'VIDEO', file: File) {
    this.gcsLocationTarget.value = gcsLocation
    this.messageTypeTarget.value = kind
    if (this.hasTextTarget) this.textTarget.required = false
    this.renderPreview(kind, file)
    this.syncComposer()
  }

  // Remove a staged attachment, returning the composer to a text-only state.
  clear() {
    this.gcsLocationTarget.value = ''
    this.messageTypeTarget.value = ''
    this.fileTarget.value = ''
    if (this.hasTextTarget)
      this.textTarget.required = this.textInitiallyRequired
    this.hidePreview()
    this.syncComposer()
  }

  // Clear the staged attachment on submit, after Turbo has serialized the form
  // for the POST (next frame) — same approach message-composer uses for the text
  // input, so the preview never lingers if the server's composer replace is slow
  // or missed. Only fires when the form actually submits (HTML validation, e.g. a
  // required subject, blocks the submit event entirely).
  reset() {
    window.requestAnimationFrame(() => this.clear())
  }

  private renderPreview(kind: 'IMAGE' | 'VIDEO', file: File) {
    if (!this.hasPreviewTarget) return
    this.revokeObjectUrl()

    const thumb =
      kind === 'IMAGE'
        ? (() => {
            this.objectUrl = URL.createObjectURL(file)
            return `<img src="${this.objectUrl}" alt="" class="h-10 w-10 rounded object-cover shrink-0" />`
          })()
        : `<span class="flex h-10 w-10 items-center justify-center rounded bg-base-300 text-xs shrink-0">▶</span>`

    this.previewTarget.innerHTML = `
      <div class="flex items-center gap-2 rounded-lg border border-base-300 bg-base-100 p-2">
        ${thumb}
        <span class="text-base-content flex-1 truncate text-sm">${escapeHtml(file.name)}</span>
        <button type="button" aria-label="Remove attachment"
          class="text-secondary-content hover:text-base-content shrink-0 px-1"
          data-action="click->balance-message-attachment#clear">✕</button>
      </div>`
    this.previewTarget.hidden = false
  }

  private hidePreview() {
    this.revokeObjectUrl()
    if (!this.hasPreviewTarget) return
    this.previewTarget.innerHTML = ''
    this.previewTarget.hidden = true
  }

  private revokeObjectUrl() {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl)
      this.objectUrl = null
    }
  }

  // Nudge the px message-composer (which gates the Send button) to re-evaluate,
  // so a staged attachment enables Send even with an empty caption. The composer
  // listens for `input` on the caption field; dx composers have no such gate.
  private syncComposer() {
    if (this.hasTextTarget) {
      this.textTarget.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  private setProgress(percent: number) {
    if (!this.hasProgressTarget) return
    this.progressTarget.hidden = false
    this.progressTarget.value = percent
  }

  private hideProgress() {
    if (!this.hasProgressTarget) return
    this.progressTarget.hidden = true
    this.progressTarget.value = 0
  }
}

function escapeHtml(value: string): string {
  const el = document.createElement('span')
  el.textContent = value
  return el.innerHTML
}
